import { Typography, Box } from '@mui/material';
import { t } from 'i18next';
import { ErrorProps } from '../../types/errorhandling.types';
import { useMemo } from 'react';
import { AlertMessage } from '../AlertMessage';
import { translateFieldError } from '../../utils/apiErrors';

const ErrorBanner = ({
  errors,
  errorMessage,
  severity,
  layer,
  styles,
}: ErrorProps) => {
  const reasons = useMemo(() => (errors ? Object.keys(errors) : []), [errors]);
  const marginDistance = useMemo(() => {
    if (layer === 1) {
      return 64 * layer;
    }
    return 54 * layer;
  }, [layer]);

  const alertHeight = useMemo(() => {
    if (!errors || reasons.length <= 1) return 96;
    return 54 * reasons.length;
  }, [errors, reasons.length]);

  return (
    <AlertMessage
      style={styles}
      severity={severity}
      title={
        <Typography
          variant="h3"
          sx={{ fontSize: 16, textTransform: 'capitalize' }}
        >
          {severity}
        </Typography>
      }
      sx={{
        mb: 12,
        position: 'sticky',
        top: marginDistance,
        zIndex: 2,
        height: alertHeight,
        '& .MuiAlert-message': {
          width: '100%',
        },
      }}
      onClick={() => {
        document.body.scrollTop = 0;
        document.documentElement.scrollTop = 0;
      }}
    >
      {errorMessage && (
        <Typography variant="subtitle1">{errorMessage}</Typography>
      )}
      {reasons &&
        reasons.map((fieldName) => {
          const fieldErrors = errors![fieldName];
          const isArray = Array.isArray(fieldErrors);

          return (
            <Box key={fieldName} sx={{ mt: 1 }}>
              <Typography variant="subtitle1">
                {t(`branding.fields.${fieldName}`, { defaultValue: fieldName })}
                :
              </Typography>
              {isArray ? (
                fieldErrors.map((error, index) => (
                  <Typography
                    key={`${fieldName}-${index}`}
                    variant="body1"
                    sx={{ ml: 2 }}
                  >
                    • {translateFieldError(t, error)}
                  </Typography>
                ))
              ) : (
                <Typography variant="body1" sx={{ ml: 2 }}>
                  • {translateFieldError(t, String(fieldErrors))}
                </Typography>
              )}
            </Box>
          );
        })}
    </AlertMessage>
  );
};

export default ErrorBanner;
